/**
 * QuestionCard — Expandable card for a single LPA question.
 *
 * Shows a compact preview (question text + tags + status) that can be
 * expanded to reveal explanation, reaction plan, and metadata.
 */

import { useState } from 'react';
import { useLanguage } from '@/core/LanguageContext';
import type { LpaQuestionWithTags } from '../types';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/ui/collapsible';
import {
  ChevronDown,
  Clock,
  Pencil,
  Trash2,
  GripVertical,
} from 'lucide-react';

interface QuestionCardProps {
  question: LpaQuestionWithTags;
  onEdit: (question: LpaQuestionWithTags) => void;
  onDelete: (question: LpaQuestionWithTags) => void;
}

/** Format seconds into a human-readable duration (e.g. "30s", "3 min"). */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.round(seconds / 60);
  return `${mins} min`;
}

export default function QuestionCard({
  question,
  onEdit,
  onDelete,
}: QuestionCardProps) {
  const { t, getTranslation } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  const questionText = getTranslation(
    question.question_translations,
    question.question,
  );
  const explanationText = getTranslation(
    question.explanation_translations,
    question.explanation ?? '',
  );
  const reactionPlanText = getTranslation(
    question.reaction_plan_translations,
    question.reaction_plan ?? '',
  );

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card
        className={`transition-colors ${
          !question.is_active ? 'opacity-60' : ''
        } ${isOpen ? 'ring-1 ring-primary/20' : ''}`}
      >
        {/* Compact header — always visible */}
        <CollapsibleTrigger asChild>
          <div className="flex items-start gap-3 p-4 cursor-pointer group select-none">
            {/* Drag handle placeholder + expand indicator */}
            <div className="flex flex-col items-center gap-1 pt-0.5 flex-shrink-0">
              <GripVertical className="h-4 w-4 text-muted-foreground/40" />
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              {/* Question text */}
              <p
                className={`text-sm font-medium leading-snug ${
                  !question.is_active
                    ? 'line-through text-muted-foreground'
                    : ''
                }`}
              >
                {questionText}
              </p>

              {/* Tags + metadata row */}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {question.tags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant="outline"
                    className="text-xs px-1.5 py-0"
                    style={
                      tag.color
                        ? { borderColor: tag.color, color: tag.color }
                        : undefined
                    }
                  >
                    {getTranslation(tag.name_translations, tag.name)}
                  </Badge>
                ))}

                {question.estimated_duration_seconds != null &&
                  question.estimated_duration_seconds > 0 && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground ml-1">
                      <Clock className="h-3 w-3" />
                      {formatDuration(question.estimated_duration_seconds)}
                    </span>
                  )}

                {!question.is_active && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    {t('mod_lpa.questions.inactive', 'Inactive')}
                  </Badge>
                )}
              </div>
            </div>

            {/* Right side: actions + chevron */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(question);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(question);
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </div>
          </div>
        </CollapsibleTrigger>

        {/* Expanded content */}
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-0 space-y-4 border-t mx-4 mt-0 pt-4">
            {/* Explanation */}
            {(question.explanation || explanationText) && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                  {t('mod_lpa.questions.explanation', 'Explanation / Guidance')}
                </h4>
                <p className="text-sm text-foreground/80 whitespace-pre-wrap">
                  {explanationText || (
                    <span className="italic text-muted-foreground">
                      {t('mod_lpa.questions.noExplanation', 'No explanation provided')}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Reaction plan */}
            {(question.reaction_plan || reactionPlanText) && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">
                  {t('mod_lpa.questions.reactionPlan', 'Reaction Plan')}
                </h4>
                <p className="text-sm text-foreground/80 whitespace-pre-wrap">
                  {reactionPlanText || (
                    <span className="italic text-muted-foreground">
                      {t('mod_lpa.questions.noReactionPlan', 'No reaction plan provided')}
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Metadata row — show if nothing above or always for completeness */}
            {!question.explanation && !question.reaction_plan && (
              <p className="text-sm text-muted-foreground italic">
                {t('mod_lpa.questions.noDetails', 'No additional details for this question.')}
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
